import { supabase } from '@/lib/integrations/supabase'
import type {
  FileLayout,
  FileLayoutField,
  FileFieldMapping,
  FileLayoutVersion,
  FileLayoutWithVersion,
  SaveFileLayoutInput,
  SavedFileLayoutReference,
} from '@/modules/file-reader/fileReaderTypes'

async function hydrateLayouts(layouts: FileLayout[]): Promise<FileLayoutWithVersion[]> {
  return Promise.all(layouts.map(async (layout) => {
    const { data: versions, error: versionError } = await supabase
      .from('file_layout_versions')
      .select('*')
      .eq('layout_id', layout.id)
      .order('version', { ascending: false })
      .limit(1)

    if (versionError) throw versionError
    const latestVersion = (versions?.[0] || null) as FileLayoutVersion | null
    let fields: FileLayoutField[] = []
    let mappings: FileFieldMapping[] = []

    if (latestVersion) {
      const { data: fieldData, error: fieldError } = await supabase
        .from('file_layout_fields')
        .select('*')
        .eq('layout_version_id', latestVersion.id)
        .order('ordinal')

      if (fieldError) throw fieldError
      fields = (fieldData || []) as FileLayoutField[]

      if (fields.length > 0) {
        const { data: mappingData, error: mappingError } = await supabase
          .from('file_field_mappings')
          .select('*')
          .in('layout_field_id', fields.map((field) => field.id))

        if (mappingError) throw mappingError
        mappings = (mappingData || []) as FileFieldMapping[]
      }
    }

    return { ...layout, latest_version: latestVersion, fields, mappings }
  }))
}

export async function listFileLayouts(unitId: string): Promise<FileLayoutWithVersion[]> {
  const { data, error } = await supabase
    .from('file_layouts')
    .select('*')
    .eq('unit_id', unitId)
    .eq('is_active', true)
    .order('updated_at', { ascending: false })

  if (error) throw error
  return hydrateLayouts((data || []) as FileLayout[])
}

export async function findMatchingFileLayouts(
  unitId: string,
  rootName: string,
  namespaceUri: string | null,
): Promise<FileLayoutWithVersion[]> {
  let query = supabase
    .from('file_layouts')
    .select('*')
    .eq('unit_id', unitId)
    .eq('format', 'xml')
    .eq('root_name', rootName)
    .eq('is_active', true)

  query = namespaceUri
    ? query.eq('namespace_uri', namespaceUri)
    : query.is('namespace_uri', null)

  const { data: layouts, error } = await query.order('updated_at', { ascending: false })
  if (error) throw error

  return hydrateLayouts((layouts || []) as FileLayout[])
}

export async function saveFileLayoutVersion(
  input: SaveFileLayoutInput,
): Promise<SavedFileLayoutReference> {
  const { data, error } = await supabase.rpc('save_file_layout_version', {
    p_unit_id: input.unitId,
    p_context_key: input.contextKey,
    p_layout_id: input.layoutId || null,
    p_name: input.name.trim(),
    p_format: input.format,
    p_root_name: input.rootName,
    p_namespace_uri: input.namespaceUri,
    p_parser_config: input.parserConfig,
    p_fields: input.fields.map((field) => ({
      source_path: field.sourcePath,
      field_key: field.fieldKey,
      label: field.label,
      node_kind: field.nodeKind,
      data_type: field.dataType,
      required: field.required,
      is_collection: field.isCollection,
      ordinal: field.ordinal,
    })),
    p_mappings: input.mappings.map((mapping) => ({
      source_path: mapping.sourcePath,
      target_entity: mapping.targetEntity,
      target_field: mapping.targetField,
      loop_path: mapping.loopPath,
    })),
  })

  if (error) throw error
  const saved = Array.isArray(data) ? data[0] : data
  if (!saved) throw new Error('file_layout_save_failed')
  return saved as SavedFileLayoutReference
}

export async function deactivateFileLayout(unitId: string, layoutId: string): Promise<void> {
  const { error } = await supabase
    .from('file_layouts')
    .update({ is_active: false })
    .eq('id', layoutId)
    .eq('unit_id', unitId)

  if (error) throw error
}
